import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Button } from 'src/components/Button';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { useAccount } from 'src/providers/account/hook/useAccount';
import { useConnectors } from 'src/providers/connectors/hook/useConnectors';
import { ROUTE_SOURCES } from 'src/constants/routeSources';
import { ReactComponent as MagentoIcon } from 'src/icons/magento.svg';
import {
  ADD_MAGENTO_CONNECTOR,
  FORCE_MAGENTO_SYNC,
  TEST_MAGENTO_CONNECTION,
} from 'src/utils/gql/mutations';
import { GET_CONNECTORS, GET_MAGENTO_SYNC_STATUS } from 'src/utils/gql/queries';

type TestResult = {
  ok: boolean;
  authMode: string;
  baseUrl: string;
  error?: string | null;
  currency?: string | null;
  timezone?: string | null;
};

export const MagentoConnector: React.FC = () => {
  const navigate = useNavigate();
  const showMessage = useSnackbar();
  const { user } = useAccount();
  const { magentoConnectorId } = useConnectors();

  useEffect(() => {
    if (user?.id && user.magentoConnectorEnabled !== true) {
      showMessage(
        'Magento connector access not authorized. Contact the administrator.',
        'warning',
      );
      navigate(ROUTE_SOURCES.CONNECTORS);
    }
  }, [user?.id, user?.magentoConnectorEnabled, navigate, showMessage]);

  const [baseUrl, setBaseUrl] = useState('');
  const [consumerKey, setConsumerKey] = useState('');
  const [consumerSecret, setConsumerSecret] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [accessTokenSecret, setAccessTokenSecret] = useState('');
  const [storeViewCode, setStoreViewCode] = useState('');
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const { data: syncStatusData, refetch: refetchSyncStatus } = useQuery(
    GET_MAGENTO_SYNC_STATUS,
    {
      skip: !magentoConnectorId,
      fetchPolicy: 'network-only',
      pollInterval: magentoConnectorId ? 5000 : 0,
    },
  );
  const syncStatus = syncStatusData?.getMagentoSyncStatus?.status as
    | string
    | undefined;

  const [testConnection, { loading: testing }] = useMutation(
    TEST_MAGENTO_CONNECTION,
  );
  const [addConnector, { loading: saving }] = useMutation(ADD_MAGENTO_CONNECTOR, {
    refetchQueries: [{ query: GET_CONNECTORS }],
  });
  const [forceSync, { loading: syncing }] = useMutation(FORCE_MAGENTO_SYNC, {
    onCompleted: () => {
      showMessage('Magento sync started', 'success');
      void refetchSyncStatus();
    },
    onError: (error) => {
      showMessage(error.message || 'Could not start Magento sync', 'error');
    },
  });

  const canSubmit = useMemo(
    () =>
      Boolean(
        baseUrl.trim() &&
          consumerKey.trim() &&
          consumerSecret.trim() &&
          accessToken.trim() &&
          accessTokenSecret.trim(),
      ),
    [accessToken, accessTokenSecret, baseUrl, consumerKey, consumerSecret],
  );

  const input = {
    baseUrl: baseUrl.trim(),
    consumerKey: consumerKey.trim(),
    consumerSecret: consumerSecret.trim(),
    accessToken: accessToken.trim(),
    accessTokenSecret: accessTokenSecret.trim(),
    storeViewCode: storeViewCode.trim() || undefined,
  };

  const handleTest = async () => {
    try {
      const { data } = await testConnection({ variables: { input } });
      const result = data?.testMagentoConnection as TestResult;
      setTestResult(result);
      if (result?.ok) {
        showMessage('Connection verified successfully', 'success');
      } else {
        showMessage(result?.error || 'Could not verify connection', 'error');
      }
    } catch (error: any) {
      showMessage(error?.message || 'Could not verify connection', 'error');
    }
  };

  const handleConnect = async () => {
    try {
      await addConnector({ variables: { input } });
      showMessage('Magento store connected', 'success');
      navigate(`/${ROUTE_SOURCES.CONNECTORS}`);
    } catch (error: any) {
      showMessage(error?.message || 'Could not connect Magento store', 'error');
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 560,
        mx: 'auto',
        py: { xs: 2, md: 3 },
        px: { xs: 2, md: 0 },
      }}
    >
      <Button
        variant="text"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/${ROUTE_SOURCES.CONNECTORS}`)}
        sx={{
          mb: 2,
          px: 0,
          color: 'text.secondary',
          textTransform: 'none',
          '&:hover': { background: 'transparent', color: 'text.primary' },
        }}
      >
        Back to Connectors
      </Button>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 1,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '10px',
            background: 'rgba(239, 239, 244, 1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            '& svg': { width: 32, height: 32 },
          }}
        >
          <MagentoIcon />
        </Box>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: 24, md: 28 },
              fontWeight: 700,
              lineHeight: 1.2,
              color: 'primary.main',
            }}
          >
            Magento
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Adobe Commerce / Magento 2
          </Typography>
        </Box>
      </Box>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 3, maxWidth: 480, lineHeight: 1.5 }}
      >
        Connect your Magento store to sync orders and sales metrics into Kipify.
        Credentials are encrypted and used only to read your store data.
      </Typography>

      {magentoConnectorId && (
        <Box
          sx={{
            mb: 3,
            borderRadius: '10px',
            background: 'rgba(239, 239, 244, 1)',
            p: 2,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Data sync
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Status:{' '}
              {syncStatus === 'in_progress'
                ? 'In progress'
                : syncStatus === 'success'
                  ? 'Up to date'
                  : syncStatus === 'fail'
                    ? 'Last sync failed'
                    : 'Not started'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={() => forceSync()}
            disabled={syncing || syncStatus === 'in_progress'}
          >
            {syncing || syncStatus === 'in_progress' ? (
              <CircularProgress size={20} />
            ) : (
              'Sync now'
            )}
          </Button>
        </Box>
      )}

      <Box
        sx={{
          borderRadius: '10px',
          background: 'rgba(239, 239, 244, 1)',
          p: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}
          >
            Store
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Store URL"
              placeholder="https://your-store.com"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              fullWidth
              required
              helperText="The public URL of your Magento storefront"
              sx={fieldSx}
            />
            <TextField
              label="Store view code"
              placeholder="default"
              value={storeViewCode}
              onChange={(e) => setStoreViewCode(e.target.value)}
              fullWidth
              helperText="Optional — leave empty to use the default store view"
              sx={fieldSx}
            />
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'rgba(221, 221, 234, 1)' }} />

        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}
          >
            Integration credentials
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Consumer Key"
              value={consumerKey}
              onChange={(e) => setConsumerKey(e.target.value)}
              fullWidth
              required
              sx={fieldSx}
            />
            <TextField
              label="Consumer Secret"
              type="password"
              value={consumerSecret}
              onChange={(e) => setConsumerSecret(e.target.value)}
              fullWidth
              required
              autoComplete="new-password"
              sx={fieldSx}
            />
            <TextField
              label="Access Token"
              type="password"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              fullWidth
              required
              autoComplete="new-password"
              sx={fieldSx}
            />
            <TextField
              label="Access Token Secret"
              type="password"
              value={accessTokenSecret}
              onChange={(e) => setAccessTokenSecret(e.target.value)}
              fullWidth
              required
              autoComplete="new-password"
              sx={fieldSx}
            />
          </Box>
        </Box>

        {testResult && (
          <Alert
            severity={testResult.ok ? 'success' : 'error'}
            sx={{ borderRadius: '8px' }}
          >
            {testResult.ok
              ? [
                  'Store reachable',
                  testResult.currency,
                  testResult.timezone,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : testResult.error || 'Connection failed'}
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: 1.5,
            justifyContent: 'flex-end',
            pt: 0.5,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleTest}
            disabled={!canSubmit || testing || saving}
            sx={{ textTransform: 'none', borderRadius: '8px' }}
          >
            {testing ? <CircularProgress size={20} /> : 'Verify connection'}
          </Button>
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={!canSubmit || testing || saving}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              bgcolor: '#222822',
              color: '#1EED9F',
              '&:hover': { bgcolor: '#1a1f1a' },
            }}
          >
            {saving ? <CircularProgress size={20} /> : 'Connect Magento'}
          </Button>
        </Box>
      </Box>

      <Accordion
        disableGutters
        elevation={0}
        sx={{
          mt: 2,
          background: 'transparent',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ px: 0, minHeight: 40, '& .MuiAccordionSummary-content': { my: 1 } }}
        >
          <Typography variant="body2" color="text.secondary">
            Where do I find these credentials?
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            In Magento Admin, go to System → Extensions → Integrations, create a
            new integration with API access to Sales, Catalog and Customers, then
            activate it. Magento will show the four keys to paste above.
          </Typography>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#fff',
    borderRadius: '8px',
  },
};

export default MagentoConnector;
